/**
 * The follow-up to "Player development in the NBA is changing": Coleman's
 * field notes to an NBA staff on taking the CLA and skill acquisition science
 * a few layers deeper. A free, public article, same treatment as the first.
 *
 * VERBATIM, like the first piece. The words are Coleman's. The only changes
 * made here are structural, not editorial:
 *
 *   - The tracking suffix (?utm_source=chatgpt.com) was stripped off the five
 *     study links. The URLs themselves are untouched and the parser
 *     auto-links the bare addresses (inlineMarkdown.js).
 *   - His authoring markers were turned into the structure they describe,
 *     never rewritten: "(hyperlink)" after a drill name is a clip Coleman will
 *     link later, so the name stays as plain text; "VIDEO: caption = ..." and
 *     the "(CLIP OF ...)" lines become video blocks; a bare "*" list becomes a
 *     "- " list (the only bullet the article body renders).
 *   - List markers were normalised to "- ". Nothing in a list was reworded.
 *
 * Nothing was tightened, reordered or corrected. Where the source says
 * something awkwardly it still says it awkwardly. The few changes Coleman
 * signed off on after reading it: "accordance" -> "affordance" (a dictation
 * slip in Layer 1); the "Add examples!" note to self removed from the
 * cognitive-load list; and two real catching-mechanics studies dropped into
 * the Layer 3 spot where he had left "studies for here:" blank (PMC4938428 and
 * PubMed 7843318, one-handed catching grasp/coordination). The article is
 * published UNLISTED: free at #/free/cavs, on no shelf, link only.
 *
 * ── The clips ──────────────────────────────────────────────────────────────
 *
 * Every playback id in `V` was read out of the `drills` table by title (the
 * clips Coleman named), never retyped off a Mux screen. Coleman's three inline
 * "(hyperlink)" references became clips shown in context: "triangle shooting"
 * is Find a Triangle Contested Shots, the Peyton Watson tag suggestion links
 * ZOOM6 (the tag game), and the unpredictability game is Tag Ghosts. "stuff
 * like these" stays plain text: the anchor and exploration clips right below
 * that sentence are the examples it points to.
 *
 * ONE CHAPTER, on purpose, exactly like the first article: the reader's lead
 * gate is `publicMode && active > 0`, so a single-chapter article never gates
 * and captures no email. The ask is the `cta` at the end.
 *
 * ── The embedded dashboard ──────────────────────────────────────────────────
 *
 * The `afsDashboard` block in the AFS section renders AfsMockDashboard.jsx: a
 * sample AFS stats view on the current Cleveland Cavaliers roster with INVENTED
 * numbers, so a reader sees what advantage tracking would look like. No real
 * results. Wired in ArticleBlocks.jsx.
 *
 * No em dashes.
 */

// The public route is #/free/<key>. Coleman asked for a short, memorable one:
// #/free/cavs (the write-up is to the Cavaliers staff).
export const NBA_CLA_LAYERS_KEY = "cavs";

/**
 * Clip placeholders, by the role each one plays in the piece. None has a
 * playback id yet: VideoBlock shows the "Coming soon" card and uses `name`.
 * When Coleman sends a clip, set its muxPlaybackId here (read it out of the
 * `drills` table, never off the Mux Assets list, which shows asset ids that
 * do not play).
 */
const V = {
  tagGame:          "HsRqCS6OYfnqCwSSZvoWtDpSwNZezwxoD8Z0202grF1EY",   // ZOOM6
  triangleShooting: "58TwN02fgCp3FBsY7JKdAIfSSl5b00Y1vb1Oros01j9JLs",  // Find a Triangle Contested Shots
  tagGhosts:        "01Jglkm9004hszeVhvok01iZxzRUrScEBo5ePcU4FNBEOY",  // Tag Ghosts
  anchorPush:       "kSd7FmXf5z9VEzBIm01j00S3NEPbpFrCVFrI52Yh011WbE",  // In Air Push Anchor Shots
  anchorBroad:      "EhnmMVtTt00Sd402XUrB01TVphujYg9cR01o200fsPZZIcZE", // Lateral Broad Distance Balance Shots
  explorationDrift: "l9oekHwgqgRefcrVBO00402JiPcoppL5CC01KVrMxQIaTk",  // Constraint-Led Lateral Drift Shots
  rhythmMisses:     "gn00wTOV5idGWDzmiTewwugI8wDG8YcCY8GALnneBP9g",    // Tyrese Rhythm
  rhythmShooting:   "XxmY8Uu1f7Kc00yuyeerSp23uA02KUbryJ96apT0201ihrs", // Rhythm Intention Corner Drift Shots
};

export const NBA_CLA_LAYERS_MODULE = {
  key: NBA_CLA_LAYERS_KEY,
  title: "Post-Visit Write Up: Cleveland Cavaliers",
  layout: "article",
  sub: "Field notes on pain points, alive learning environments, indirect effects, and tracking advantages.",
  // A private write-up, not a lead magnet: suppress the public reader's
  // "Read more / Sign up for full access" asks (the sticky bar and the
  // end-of-chapter unlock block). The article itself carries no cta blocks.
  hidePublicCta: true,
  // Not a lesson, so no "12 min read" length line in the header: the reader
  // computes one by default (chapterMeta in MasterclassReader.jsx); this
  // suppresses it for this module only.
  hideReadTime: true,
  chapters: [
    {
      title: "Post-Visit Write Up: Cleveland Cavaliers",
      blocks: [

        { kind: "text", md:
          "Much of where I see the NBA going with the CLA & skill acquisition science is simply using live defenders to engage decision making processes." },

        { kind: "text", md:
          "We can do that without the CLA. That's the most superficial layer of this whole thing." },

        { kind: "text", md:
          "You guys are taking it multiple layers deeper than most, and it was really fun to see. The understanding of the science is definitely the highest and it seems like the staff is really fully bought in. That's truly 1 of 1." },

        { kind: "text", md:
          "And what's even more fun is that I think there are even a couple of layers deeper it can go." },

        { kind: "text", md:
          "As always, take all of this with a grain of salt, as I don't have full context, and I included almost everything that crossed my mind. But here are the primary affordances I see to dig a bit deeper:" },

        /* ── Layer 1 ──────────────────────────────────────────────────────── */
        { kind: "heading", level: 2, text: "Layer 1: understanding and targeting deep \"pain points\"" },

        { kind: "text", md:
          "For example: one \"pain point\" I noticed was Peyton Watson struggled to shoot going right (especially when paired with a bit of rotation towards his left). Small sample size (and tough to get a feel for the data because the Nuggets don't space laterally as much), but it seemed pretty visually obvious to me. Again, take it with a grain of salt as an example for HOW to think about this." },

        { kind: "text", md:
          "The PD Goal may be to shoot X% better on catch & shoot, and one pain point that's constraining that heavily is the moving to the right while rotating back to the left." },

        { kind: "text", md:
          "We could solve this with:\n\n"
          + "- Implementing a 1.5:1 ratio of R:L on reps\n"
          + "- Scoring systems where going right is an extra point.\n"
          + "- Tag\n"
          + "- Rotation\n"
          + "- No rotation\n"
          + "- Divergent footworks going right (to analyze which ones he struggles with most)" },

        { kind: "video", muxPlaybackId: V.tagGame, title: "The tag game, for the rotation constraint." },

        { kind: "text", md:
          "To take it a layer deeper, I also noticed that he was flat going right on the majority of his misses." },

        { kind: "text", md:
          "Maybe this is where you task each technical lead with developing external cues that \"click\" for them, and then even sharing them so other coaches who may work with them are aware of those cues and more importantly WHY they're being used." },

        { kind: "text", md:
          "Now, with some guys, you want to consciously bring this to their attention because they'll attack it with open arms and won't lose all confidence in it. With others, you don't because it'll shatter their confidence in going right. That dictates how you manipulate this." },

        { kind: "text", md:
          "For example, to NOT raise red flags for P-Wat, you may use more games where the coach can control direction. In \"triangle shooting\" which I saw him using a few times, you may make more passes to the right than the left. They don't know this unless it's EXTREMELY obvious but it still shapes the learning experience." },

        { kind: "video", muxPlaybackId: V.triangleShooting, title: "Triangle shooting." },

        { kind: "text", md:
          "Maybe you make intentional passes to make him reach for it and rotate back when going right." },

        { kind: "text", md:
          "Or add in a second passer to his right that may pass the ball from there too to force him to catch and rotate over left shoulder a bit." },

        { kind: "text", md:
          "There are a million ways to do this, but notice how these solutions stem from solving a problem for the player with deep considerations vs. \"what drill should I put them through today?\"" },

        { kind: "text", md: "That's intentionality." },

        { kind: "text", md:
          "And again, MAYBE it was just that day. Maybe it was even just in that single drill." },

        { kind: "text", md:
          "But even still, if a player is showing signs of being flat one day, quickly identifying this as a coach and working them through how to adjust and find cues with limited verbal coaching is a great learning experience that builds an understanding and competency to adjust autonomously." },

        { kind: "text", md:
          "Maybe it's less of a movement skill or \"action capacity\" deficiency and more of a perceptual one, where you notice a player isn't acting on an affordance you want them to." },

        { kind: "text", md:
          "This may be, for example, Meleek struggling against \"down\" coverage. Random example here…" },

        { kind: "text", md:
          "First-layer thinking would be: let's rep out every coverage with our PGs." },

        { kind: "text", md:
          "Second-layer thinking would be: what coverages is Meleek struggling against? Let's rep out those and use a constraint to play primarily against \"down.\" Some NBA teams are here." },

        { kind: "text", md:
          "Third-layer thinking would be: let's look at the affordance landscape of \"down\" and which solutions/affordances he's shying away from, then incentivize him to attack those a bit more with a point system (e.g., he's not very aggressive using his speed to try to turn the corner and extend the big, so we give him an extra point if he's able to attack and touch the big's far hip). This is surprisingly rare in the NBA from my experience." },

        { kind: "text", md:
          "Fourth-layer thinking would be: asking \"why he is shying away from that specific solution?\" Maybe it's just that he's never been cued to do it, but maybe by exploring that, we identify that he's not very confident working through the hip of a physical big in small spaces going to his left. So we isolate that \"pain point.\" Maybe this is too much detail, but thinking through affordances with this lens has helped us a lot." },

        { kind: "text", md:
          "Maybe Sam, P-Wat, and Tyrese are struggling with getting steered as the season goes on, whereas JT is elite at avoiding it. So you throw them all in a group with JT as a model and use a variety of constraints (e.g., this one for unpredictability to create early separation, a game that incentivizes swimming over the top, etc.) to identify solutions to avoid it. And through this you identify which solutions each player struggles with, which ones you should try to turn into a habit, and then implement that into their individual PD." },

        { kind: "video", muxPlaybackId: V.tagGhosts, title: "Tag ghosts: unpredictability, to create early separation." },

        { kind: "text", md:
          "In a perfect world, we should be on a HUNT to discover these specific pain points and be conscious of them when applying constraints or designing environments." },

        /* ── Layer 2 ──────────────────────────────────────────────────────── */
        { kind: "heading", level: 2, text: "Layer 2) More \"alive\" learning environments." },

        { kind: "text", md:
          "Most of what I see in the CLA realm are \"dead\" learning environments. The coach sets up a game, maybe adds a constraint, and it goes the same way until it's over. Plus, everything is pre-planned and not very responsive to the athlete at hand." },

        { kind: "text", md:
          "One of the gold standards of coaches using the CLA is being able to adjust the game or task on the fly to:\n\n"
          + "- Add/reduce difficulty\n"
          + "- Change the target skill/decision because it's not exactly what you want\n"
          + "- Change the target skill/decision because you want to create new problems for them to solve" },

        { kind: "text", md:
          "Especially with a bit more intentionality and specificity, you'll find yourself needing to do this a bit more." },

        { kind: "text", md:
          "We shouldn't be changing constraints JUST to change constraints, of course, but I think there's definitely more room for the staff to adjust constraints on the fly" },

        { kind: "text", md:
          "As mentioned, many times it's to adjust difficulty when they may outperform your expectations above the Optimal Zone of Improvement, or when the task just didn't shape out like you thought it would." },

        { kind: "text", md:
          "But sometimes, these guys are so good, that they solve the problem very quickly. So you may add in another constraint to quickly change the problem solving landscape." },

        { kind: "text", md:
          "For instance, in a drop ball screen 2v2, every rep or every few reps you throw a different constraint at them:\n\n"
          + "- Must take 4 hostage dribbles without allowing the defender to get back in front\n"
          + "- Has to be a right - right finish\n"
          + "- Can only be 3 or layup\n"
          + "- Can't dribble with the right hand" },

        { kind: "text", md:
          "Some are more targeted, some are more random, but you're just throwing different problems at them to dial up the challenge. I almost consider this differential learning for decision making." },

        /* ── Layer 3 ──────────────────────────────────────────────────────── */
        { kind: "heading", level: 2, text: "Layer 3) Thinking of indirect effects" },

        { kind: "text", md: "Ernest's catching comes to mind here." },

        { kind: "text", md:
          "From the surface, the solution may be to throw variable passes and have him catch it" },

        { kind: "text", md: "But there's some interesting research about catching:" },

        { kind: "text", md:
          "First around the mechanics of catching that I'll leave some studies for here: https://pmc.ncbi.nlm.nih.gov/articles/PMC4938428/ and https://pubmed.ncbi.nlm.nih.gov/7843318/" },

        { kind: "text", md:
          "But more interestingly to me, around the attentional demand of post-catch decisions, and how much that can hinder one's catching ability." },

        { kind: "text", md:
          "It's like in football. Guys hear footsteps, start looking up field, their \"attentional currency\" shifts towards that, and the cognitive load is too much for them to execute a skill they've done thousands of times: catch the rock. There's an interesting study on this: https://pubmed.ncbi.nlm.nih.gov/26982371/" },

        { kind: "text", md: "We can approach this in two ways:" },

        { kind: "text", md:
          "- 1, decrease the attention required to catch the ball.\n"
          + "- 2, decrease the attention required to make the post-catch decision." },

        { kind: "text", md:
          "The first one involves more 1-person standard catching drills. Maybe some added head movement, peripheral vision training, varied size balls (eg tennis balls), etc. (Check out this study: https://pubmed.ncbi.nlm.nih.gov/27392041/)" },

        { kind: "text", md:
          "The second one is where decisions should be involved and where the perception-action cycle is so important." },

        { kind: "text", md: "Both are essential." },

        { kind: "text", md:
          "But it's just interesting to think that the main constraint to E's catching may not be simply catching the ball, but the amount of attention that goes towards what comes after it. And by emphasizing and overloading that subsequent decision, it may substantially cut down on the time it takes for him to develop this skill." },

        { kind: "text", md:
          "Similar idea for Riley's shot technique, which was 9th slowest in the G last year." },

        { kind: "text", md:
          "Most would simply look to speed it up with close contests, over and over again, and assume that he'll shoot it quicker in the game." },

        { kind: "text", md:
          "That may work, but it also may be a bandaid. I'd actually argue his ball speed from lift to release on static shots isn't super slow." },

        { kind: "text", md: "Where I'd look is a bit deeper:" },

        { kind: "text", md:
          "One thing I noticed is that his ability to organize his base efficiently when moving laterally was pretty limited. He often takes an extra 1-2 steps, and takes them pretty slowly." },

        { kind: "text", md: "But secondly and potentially more relevant:" },

        { kind: "text", md:
          "His average shot may have been statistically been 9th slowest not because it's the 9th slowest jumper technique, but because he didn't see the affordance to shoot quicker shots." },

        { kind: "text", md:
          "Because he likely lacks trust in his ability to get set quickly on the move and shoot a more dynamic shot, he ends up shooting less faster/more contested shots, and thus his average shot is slower in the data." },

        { kind: "text", md:
          "So now, rather than looking to speed up the shot, you're looking to build (1) immense trust in his ability to get his feet set moving laterally (probably even exaggerating it with stuff like these)." },

        // The two anchor clips are one idea shown twice: side by side in a
        // little gallery, not stacked full-width.
        { kind: "videoGallery", portrait: true, items: [
          { muxPlaybackId: V.anchorPush, caption: "Part of this is helping him find balance in crazy situations." },
          { muxPlaybackId: V.anchorBroad },
        ] },

        { kind: "video", muxPlaybackId: V.explorationDrift, portrait: true,
          caption: "And then part of this is helping him be comfortable knocking it down even when he isn't fully set." },

        { kind: "text", md:
          "And part of this may be working in more dynamic situations with tight closeouts but giving him the opportunity to choose NOT to shoot, and then giving him feedback (or allowing him to self-rate) on whether it was a shot that he should've shot or not. This helps open up new affordances to put the quicker trigger to use." },

        { kind: "rule" },

        { kind: "text", md:
          "Combine all of these layers, and you go from repping out a situation to solving problems for players." },

        { kind: "text", md:
          "There are times to simply rep out situations. But why not add a deeper, more personalized, responsive layer?" },

        /* ── Other quick thoughts ─────────────────────────────────────────── */
        { kind: "heading", level: 2, text: "Other quick thoughts" },

        { kind: "heading", level: 2, text: "Throwing strikes" },

        { kind: "text", md:
          "A good rule of thumb we use is that anything you constantly cue, try to find a constraint to maximize attention to it through behavior." },

        { kind: "text", md:
          "Maybe it was just 1-2 days, but I heard 4-5 times coaches would cue guys to make on-time, on-target passes. And I saw slow or misplaced passes kill a pretty good amount of advantages." },

        { kind: "text", md:
          "One constraint I've used is when playing by 2s and 3s, bad pass buckets are -1. So would be a 1 and 2 respectively. Could also do in more small-sided settings but usually with highly talented guys, need to be more degrees of freedom to really challenge them here." },

        { kind: "heading", level: 2, text: "Speed & rhythm on on-air volume shooting" },

        { kind: "text", md:
          "This mainly came to mind watching Tyrese, who has really nice rhythm on the catch and good overall mechanics, but he's pretty inconsistent with these quick-trigger, in-rhythm shots." },

        { kind: "text", md:
          "This isn't just him, but I've thought a lot about how more chill shots where speed is not constrained can wire the system to shoot slower and also how many opportunities we miss out on to get better at these rhythm shots, especially when a guy like him is so close to being really good at it." },

        { kind: "video", muxPlaybackId: V.rhythmMisses, portrait: true },

        { kind: "text", md:
          "I started making this more of a theme/intention on more on-air shots:" },

        { kind: "video", muxPlaybackId: V.rhythmShooting, portrait: true },

        { kind: "text", md:
          "I also know from first hand experience that guys love getting their slow shots up. Just something to think about." },

        { kind: "heading", level: 2, text: "Pairings/pods" },

        { kind: "text", md:
          "Obviously the nature of the NBA is very individualized and this requires more of a structural change to practices/indy's, but I've always been a fan of pairing players, for a few reasons:" },

        { kind: "text", md:
          "1, the primary one, to work on decision making in a way that also builds shared cognition (chemistry). For instance, instead of having 2 coaches on D, with Craig making pocket reads with a coach in the dunker. Pair him up with a guy he's commonly on the court with in those situations so now they're getting a ton of repetition without repetition reading each other's postural cues. I'd imagine this would primarily be bigs / guards PnR pairings but also some common small / small pairings." },

        { kind: "text", md:
          "Relevant studies here: https://pmc.ncbi.nlm.nih.gov/articles/PMC5581343/ and https://pmc.ncbi.nlm.nih.gov/articles/PMC5030782/" },

        { kind: "text", md:
          "Second reason being that competition and observational learning are very strong forces that I've always felt like I miss out on with players when I start to over-individualize stuff." },

        { kind: "text", md: "Just a thought." },

        { kind: "heading", level: 2, text: "Cognitive load" },

        { kind: "text", md:
          "DL shooting is often used to challenge the movement pattern / system to adapt to the extremes, with the goal of creating challenge for highly skilled athletes." },

        { kind: "text", md:
          "And sometimes, guys are still so good at shooting that you have to add more to spice it up." },

        { kind: "text", md:
          "Something I think guys could experiment with more is overloading the cognitive side of things." },

        { kind: "text", md:
          "- Adding 3 decisions.\n"
          + "- Having them solve a math problem or call a number when shooting.\n"
          + "- Challenging the quiet eye a bit more.\n"
          + "- Catching a tennis ball after the shot." },

        { kind: "text", md:
          "You'll often see the success rate drop significantly not because of any movement variability, but because of the cognitive load added that challenges their ability to control their attention and the automaticity of the movement pattern." },

        { kind: "text", md:
          "Research: https://pmc.ncbi.nlm.nih.gov/articles/PMC10881661/" },

        /* ── AFS ──────────────────────────────────────────────────────────── */
        { kind: "heading", level: 2, text: "AFS" },

        { kind: "text", md:
          "Corey and I discussed this, but I think tracking advantages would be incredibly useful. I have been using a software we built that streamlines the process and gives stats like:\n\n"
          + "- Advantage creation rate\n"
          + "- Which players are best at maintaining advantages\n"
          + "- Save rate (how many advantage possessions you stifle/save defensively)\n"
          + "- Which tactics create the most advantages\n"
          + "- How advantages are most commonly lost.\n"
          + "- Many more possibilities." },

        { kind: "afsDashboard", team: "Cleveland Cavaliers" },

        { kind: "text", md:
          "I'm happy to send over what we use and help out in any way on this. It's provided some super interesting coaching/practice design implications for us." },
      ],
    },
  ],
};

export default NBA_CLA_LAYERS_MODULE;
